/*
 * Purpose: Mission live-event notifications — toasts with scroll actions
 * Last modified: 2026-06-28 | Agent
 * Completeness: 100
 */
import { toast } from 'sonner';
import type { MissionDossierActor, MissionDossierKind } from '@/services/mission-dossier/types';
import type { MissionTaskReceipt } from '@/services/mission-receipts/types';

export const MISSION_NOTIFY_ANCHORS = {
  receipts: 'mission-task-receipts',
  dossier: 'mission-dossier',
  plan: 'mission-plan-review',
} as const;

export type MissionNotifyAnchor = keyof typeof MISSION_NOTIFY_ANCHORS;

export function scrollToMissionSection(anchor: MissionNotifyAnchor | string): void {
  if (typeof document === 'undefined') return;
  const id =
    anchor in MISSION_NOTIFY_ANCHORS
      ? MISSION_NOTIFY_ANCHORS[anchor as MissionNotifyAnchor]
      : anchor;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  el.classList.add('ring-2', 'ring-primary/40', 'rounded-md');
  window.setTimeout(() => {
    el.classList.remove('ring-2', 'ring-primary/40', 'rounded-md');
  }, 1800);
}

const recentKeys = new Map<string, number>();
const DEBOUNCE_MS = 1200;

function shouldNotify(key: string): boolean {
  const now = Date.now();
  const last = recentKeys.get(key) ?? 0;
  if (now - last < DEBOUNCE_MS) return false;
  recentKeys.set(key, now);
  return true;
}

function receiptAction(label = 'View receipt') {
  return {
    label,
    onClick: () => scrollToMissionSection('receipts'),
  };
}

function planAction(label = 'Review plan') {
  return {
    label,
    onClick: () => scrollToMissionSection('plan'),
  };
}

/** Notify user when a mission task receipt is written (live documentation). */
export function notifyMissionTaskReceipt(
  receipt: MissionTaskReceipt,
  kind: MissionDossierKind,
  actor: MissionDossierActor
): void {
  const key = `${kind}:${receipt.seq}:${receipt.summary.slice(0, 48)}`;
  if (!shouldNotify(key)) return;

  const mtr = `MTR #${String(receipt.seq).padStart(3, '0')}`;
  const description = receipt.summary.slice(0, 140);

  switch (kind) {
    case 'error':
      toast.error(receipt.title, {
        description,
        action: receiptAction('View log'),
      });
      return;

    case 'plan':
    case 'director':
    case 'connector_built':
    case 'map_patch':
      toast.success(`Plan updated · ${mtr}`, {
        description: receipt.mapDelta
          ? `${description} (+${receipt.mapDelta.addedNodes ?? 0} blocks)`
          : description,
        action: planAction(),
      });
      return;

    case 'interpret':
      toast.message(`Outcome interpreted · ${mtr}`, {
        description,
        action: receiptAction(),
      });
      return;

    case 'discovery':
      toast.message(`Integrations discovered · ${mtr}`, {
        description,
        action: receiptAction('View proposals'),
      });
      return;

    case 'dry_run':
      toast.message(`Dry run complete · ${mtr}`, {
        description,
        action: planAction('View report'),
      });
      return;

    case 'approve':
    case 'live_run':
      toast.success(`${receipt.title} · ${mtr}`, {
        description,
        action: receiptAction(),
      });
      return;

    case 'risk_approval':
    case 'automation_consent':
      if (actor === 'human') return;
      toast.message(receipt.title, { description, action: receiptAction() });
      return;

    case 'mission_started':
    case 'outcome_edited':
      toast.message(receipt.title, { description });
      return;

    default:
      if (actor === 'system' || actor === 'ai') {
        toast.message(`${receipt.title} · ${mtr}`, {
          description,
          action: receiptAction(),
        });
      }
  }
}

/** Explicit plan refresh notification (e.g. connector wired on review). */
export function notifyMissionPlanUpdated(message: string, detail?: string): void {
  if (!shouldNotify(`plan-refresh:${message}`)) return;
  toast.success(message, {
    description: detail?.slice(0, 140),
    action: planAction(),
  });
}
